// src/components/LotCreator.jsx
//
// Modal de création de lot — sélection multiple d'œuvres d'une collection
// puis création d'un lot lié.
//
// Branchement dans CollectionDetail.jsx :
//   import LotCreator from "../components/LotCreator";
//   // Dans les états :
//   const [lotCreatorOpen, setLotCreatorOpen] = useState(false);
//   // Bouton dans le header (visible si canEdit) :
//   <button onClick={() => setLotCreatorOpen(true)}>Grouper en lot</button>
//   // En bas du return, avant les autres modals :
//   {lotCreatorOpen && (
//     <LotCreator
//       collectionId={collection.id}
//       clientId={user.id}
//       onClose={() => setLotCreatorOpen(false)}
//       onCreated={() => { setLotCreatorOpen(false); refreshArtworks(); }}
//     />
//   )}
//
// SQL à exécuter dans Supabase :
//   CREATE TABLE IF NOT EXISTS public.lots (
//     id uuid PRIMARY KEY DEFAULT extensions.uuid_generate_v4(),
//     client_id uuid NOT NULL,
//     collection_id uuid,
//     title text NOT NULL,
//     artist text,
//     technique text,
//     total_copies int,
//     edition_info text,
//     created_at timestamptz DEFAULT now()
//   );
//   ALTER TABLE public.artworks ADD COLUMN IF NOT EXISTS lot_id uuid REFERENCES public.lots(id) ON DELETE SET NULL;
//   ALTER TABLE public.artworks ADD COLUMN IF NOT EXISTS lot_copy_number text;

import React, { useEffect, useState, useMemo } from "react";
import { X, Layers, CheckSquare, Square, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "../lib/supabaseClient";

// existingLot (optionnel) : { id, title, artist, technique, total_copies, edition_info }
// Si fourni → mode édition (UPDATE), sinon → mode création (INSERT)

const inventoryCollator = new Intl.Collator("fr", { numeric: true, sensitivity: "base" });
const inventorySortKey = (item) => String(item?.inventory_number || item?.lot_copy_number || item?.title || "").trim();
const sortByInventory = (items = []) =>
  [...items].sort((a, b) => {
    const ak = inventorySortKey(a);
    const bk = inventorySortKey(b);
    if (!ak && bk) return 1;
    if (ak && !bk) return -1;
    const byInventory = inventoryCollator.compare(ak, bk);
    if (byInventory !== 0) return byInventory;
    return inventoryCollator.compare(String(a?.id || ""), String(b?.id || ""));
  });

export default function LotCreator({ collectionId, clientId, onClose, onCreated, existingLot, preselectedArtworkId }) {
  const isEditMode = !!existingLot;

  const [artworks,  setArtworks]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(new Set());
  const [saving,    setSaving]    = useState(false);
  const [step,      setStep]      = useState(isEditMode ? "configure" : "select");
  const [error,     setError]     = useState("");

  const [search, setSearch] = useState("");

  // Métadonnées du lot — pré-remplies si mode édition
  const [lotTitle,    setLotTitle]    = useState(existingLot?.title        || "");
  const [lotArtist,   setLotArtist]   = useState(existingLot?.artist       || "");
  const [lotTechnique,setLotTechnique]= useState(existingLot?.technique    || "");
  const [lotCopies,   setLotCopies]   = useState(existingLot?.total_copies != null ? String(existingLot.total_copies) : "");
  const [lotEdition,  setLotEdition]  = useState(existingLot?.edition_info || "");
  const [pricingMode, setPricingMode] = useState(existingLot?.pricing_mode || "per_unit");
  const [globalPrice, setGlobalPrice] = useState(existingLot?.global_price != null ? String(existingLot.global_price) : "");

  // Option d'application en masse aux fiches individuelles du lot
  const [applySignatureToWorks, setApplySignatureToWorks] = useState(false);
  const [lotSignature, setLotSignature] = useState(true);
  const [lotSignatureDesc, setLotSignatureDesc] = useState("");

  // Année de création commune (les lots partaient sans année → fiches « Inconnu »)
  const [applyYearToWorks, setApplyYearToWorks] = useState(false);
  const [lotYear, setLotYear] = useState("");

  // Charger les œuvres de la collection (sans lot existant priorité)
  useEffect(() => {
    supabase
      .from("artworks")
      .select("id, title, artist, year, medium, inventory_number, lot_id")
      .eq("collection_id", collectionId)
      .order("artist")
      .then(({ data, error }) => {
        if (error) { setError(error.message); }
        else {
          setArtworks(sortByInventory(data || []));
          // En mode édition : pré-cocher les œuvres déjà dans ce lot
          if (isEditMode && existingLot?.id) {
            const preSelected = new Set(
              (data || []).filter(a => a.lot_id === existingLot.id).map(a => a.id)
            );
            setSelected(preSelected);
          } else if (preselectedArtworkId) {
            setSelected(new Set([preselectedArtworkId]));
          } else if (preselectedArtworkId) {
            setSelected(new Set([preselectedArtworkId]));
          }
        }
        setLoading(false);
      });
  }, [collectionId]); // eslint-disable-line

  // Pré-remplir artiste + technique depuis la sélection
  useEffect(() => {
    if (selected.size === 0) return;
    const sel = artworks.filter(a => selected.has(a.id));
    const artists = [...new Set(sel.map(a => a.artist).filter(Boolean))];
    const techs   = [...new Set(sel.map(a => a.medium).filter(Boolean))];
    if (artists.length === 1 && !lotArtist) setLotArtist(artists[0]);
    if (techs.length === 1   && !lotTechnique) setLotTechnique(techs[0]);
    if (!lotTitle) setLotTitle(artists[0] ? `Lot ${artists[0]}` : "Nouveau lot");
  }, [step]); // eslint-disable-line

  const toggleArtwork = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === artworks.length) setSelected(new Set());
    else setSelected(new Set(artworks.map(a => a.id)));
  };

  const selectedList = useMemo(
    () => sortByInventory(artworks.filter(a => selected.has(a.id))),
    [artworks, selected]
  );

  // Grouper par artiste pour l'affichage
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? artworks.filter(a =>
          (a.artist || "").toLowerCase().includes(q) ||
          (a.inventory_number || "").toLowerCase().includes(q) ||
          (a.title || "").toLowerCase().includes(q)
        )
      : artworks;
    const map = {};
    filtered.forEach(a => {
      const key = a.artist || "Sans artiste";
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    Object.keys(map).forEach(key => { map[key] = sortByInventory(map[key]); });
    return Object.entries(map).sort(([a], [b]) => inventoryCollator.compare(a, b));
  }, [artworks, search]);

  const handleSave = async () => {
    if (!lotTitle.trim() || selected.size < 1) return;
    setSaving(true);
    setError("");

    try {
      const payload = {
        title:        lotTitle.trim(),
        artist:       lotArtist.trim() || null,
        technique:    lotTechnique.trim() || null,
        total_copies: lotCopies ? parseInt(lotCopies) : null,
        edition_info: lotEdition.trim() || null,
        pricing_mode: pricingMode,
        global_price: pricingMode === "global" && globalPrice ? parseFloat(globalPrice) : null,
      };

      let lotId;

      if (isEditMode) {
        // UPDATE via RPC SECURITY DEFINER — contourne la RLS pour l'expert
        const previousIds = artworks
          .filter(a => a.lot_id === existingLot.id)
          .map(a => a.id);
        const toRemove = previousIds.filter(id => !selected.has(id));

        const { error: updErr } = await supabase.rpc("update_lot_as_expert", {
          p_lot_id:       existingLot.id,
          p_title:        payload.title,
          p_artist:       payload.artist,
          p_technique:    payload.technique,
          p_total_copies: payload.total_copies,
          p_edition_info: payload.edition_info,
          p_pricing_mode: payload.pricing_mode,
          p_global_price: payload.global_price,
          p_artwork_ids:  [...selected],
          p_remove_ids:   toRemove,
        });
        if (updErr) throw updErr;
        lotId = existingLot.id;
      } else {
        // INSERT nouveau lot
        const { data: lot, error: lotErr } = await supabase
          .from("lots")
          .insert({ client_id: clientId, collection_id: collectionId, ...payload })
          .select("id")
          .single();
        if (lotErr) throw lotErr;
        lotId = lot.id;
      }

      // En mode création : rattacher les œuvres au lot.
      // En mode édition : le RPC s'en est déjà chargé (lot_id + remove).
      // Dans les deux cas : appliquer la signature en masse si demandée.
      const artworkPatch = isEditMode ? {} : { lot_id: lotId };

      if (applySignatureToWorks) {
        artworkPatch.signature = lotSignature;
        artworkPatch.signature_desc = lotSignatureDesc.trim() || null;
        artworkPatch.signature_info = {
          signed: lotSignature,
          description: lotSignatureDesc.trim(),
        };
        artworkPatch.updated_at = new Date().toISOString();
      }

      if (applyYearToWorks && String(lotYear).trim()) {
        artworkPatch.year = String(lotYear).trim();
        artworkPatch.updated_at = new Date().toISOString();
      }

      if (Object.keys(artworkPatch).length > 0) {
        const { error: updateErr } = await supabase
          .from("artworks")
          .update(artworkPatch)
          .in("id", [...selected]);
        if (updateErr) throw updateErr;
      }

      onCreated?.();
    } catch (e) {
      console.error("[LotCreator] error:", e);
      setError(e.message || "Erreur lors de la sauvegarde du lot.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: "min(720px, 95vw)", maxHeight: "90vh", fontFamily: "Georgia, serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-gray-600" />
            <div>
              <h2 className="text-lg font-serif text-gray-900">
                {step === "select"
                  ? "Sélectionner les œuvres du lot"
                  : isEditMode ? "Modifier le lot" : "Configurer le lot"}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {step === "select"
                  ? `${selected.size} sélectionnée${selected.size > 1 ? "s" : ""} sur ${artworks.length}`
                  : `${selected.size} œuvres · ${lotTitle || "Nom du lot"}`
                }
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-700" />
          </button>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ── ÉTAPE 1 : SÉLECTION ── */}
          {step === "select" && (
            <>
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              )}

              {!loading && artworks.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  Aucune œuvre dans cette collection.
                </div>
              )}

              {!loading && artworks.length > 0 && (
                <>
                  {/* Barre de recherche */}
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Filtrer par artiste, n° inventaire ou titre…"
                      className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                    />
                    <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.803 5.803a7.5 7.5 0 0 0 10.994 10.994Z" /></svg>
                    {search && (
                      <button type="button" onClick={() => setSearch("")} className="absolute right-2.5 top-2 text-gray-400 hover:text-black text-xs">✕</button>
                    )}
                  </div>

                  {/* Tout sélectionner */}
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-4 transition"
                  >
                    {selected.size === artworks.length
                      ? <CheckSquare className="w-4 h-4" />
                      : <Square className="w-4 h-4" />
                    }
                    {selected.size === artworks.length ? "Tout désélectionner" : "Tout sélectionner"}
                  </button>

                  {/* Liste groupée par artiste */}
                  <div className="space-y-4">
                    {grouped.map(([artist, works]) => (
                      <div key={artist}>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                          {artist}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {works.map(aw => {
                            const isSelected = selected.has(aw.id);
                            const hasOtherLot = !!aw.lot_id && aw.lot_id !== existingLot?.id;
                            const tooltip = [aw.title || "Sans titre", aw.artist, aw.year].filter(Boolean).join(" · ");
                            return (
                              <button
                                key={aw.id}
                                type="button"
                                title={tooltip}
                                onClick={() => !hasOtherLot && toggleArtwork(aw.id)}
                                className={[
                                  "px-2 py-0.5 rounded border text-xs transition",
                                  hasOtherLot
                                    ? "border-gray-100 text-gray-300 cursor-not-allowed"
                                    : isSelected
                                    ? "border-black bg-black text-white"
                                    : "border-gray-300 text-gray-700 hover:border-black",
                                ].join(" ")}
                              >
                                {aw.inventory_number || aw.title?.substring(0, 15) || "—"}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── ÉTAPE 2 : CONFIGURATION ── */}
          {step === "configure" && (
            <div className="space-y-5">

              {/* Récap des œuvres sélectionnées */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {selectedList.length} œuvres dans ce lot
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedList.map(aw => (
                    <span key={aw.id} className="px-2 py-0.5 rounded bg-white border border-gray-200 text-xs text-gray-700">
                      {aw.inventory_number || aw.title?.substring(0, 20) || "—"}
                    </span>
                  ))}
                </div>
              </div>

              {/* Formulaire */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du lot <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lotTitle}
                  onChange={e => setLotTitle(e.target.value)}
                  placeholder="Ex : Lithographies Van Der Meulen"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Artiste</label>
                  <input
                    type="text"
                    value={lotArtist}
                    onChange={e => setLotArtist(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Technique</label>
                  <input
                    type="text"
                    value={lotTechnique}
                    onChange={e => setLotTechnique(e.target.value)}
                    placeholder="Ex : Lithographie"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tirage total
                  </label>
                  <input
                    type="number"
                    value={lotCopies}
                    onChange={e => setLotCopies(e.target.value)}
                    placeholder="Ex : 30"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Détails édition
                  </label>
                  <input
                    type="text"
                    value={lotEdition}
                    onChange={e => setLotEdition(e.target.value)}
                    placeholder="Ex : 1/30 à 6/30 + 2 EA"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  />
                </div>
              </div>

              {/* ── Mode de valorisation ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mode de valorisation</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPricingMode("per_unit")}
                    className="flex-1 flex flex-col items-start px-4 py-3 rounded-lg border text-sm transition"
                    style={{ borderColor: pricingMode === "per_unit" ? "#1a1a1a" : "#e5e3df", background: pricingMode === "per_unit" ? "#1a1a1a" : "#fff", color: pricingMode === "per_unit" ? "#fff" : "#555" }}
                  >
                    <span className="font-semibold mb-0.5">Par exemplaire</span>
                    <span style={{ fontSize: 11, opacity: 0.75 }}>Sérigraphies, éditions — chaque œuvre a son propre prix</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPricingMode("global")}
                    className="flex-1 flex flex-col items-start px-4 py-3 rounded-lg border text-sm transition"
                    style={{ borderColor: pricingMode === "global" ? "#1a1a1a" : "#e5e3df", background: pricingMode === "global" ? "#1a1a1a" : "#fff", color: pricingMode === "global" ? "#fff" : "#555" }}
                  >
                    <span className="font-semibold mb-0.5">Prix global (installation)</span>
                    <span style={{ fontSize: 11, opacity: 0.75 }}>Polyptique, installation — le lot a un prix unique indivisible</span>
                  </button>
                </div>
              </div>

              {pricingMode === "global" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix global du lot (EUR)</label>
                  <input
                    type="number"
                    value={globalPrice}
                    onChange={e => setGlobalPrice(e.target.value)}
                    placeholder="Ex : 20000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  />
                  <p className="text-xs text-gray-400 mt-1">Ce prix s'applique à l'ensemble du lot. Les fiches individuelles afficheront ce montant en lecture seule.</p>
                </div>
              )}

              {/* ── Application en masse : signature du lot ── */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={applySignatureToWorks}
                    onChange={e => setApplySignatureToWorks(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="font-semibold">Appliquer une signature commune aux œuvres du lot</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Utile pour les éditions : “signée au recto”, “numérotée au recto”, etc. Cela remplira les champs signature des fiches individuelles sélectionnées.
                    </span>
                  </span>
                </label>

                {applySignatureToWorks && (
                  <div className="mt-4 grid gap-3">
                    <label className="flex items-center gap-3 text-sm">
                      <input
                        type="checkbox"
                        checked={lotSignature}
                        onChange={e => setLotSignature(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      Œuvres signées
                    </label>
                    <input
                      type="text"
                      value={lotSignatureDesc}
                      onChange={e => setLotSignatureDesc(e.target.value)}
                      placeholder="Ex : Signées au recto et numérotées au recto"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>
                )}
              </div>

              {/* ── Application en masse : année de création ── */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={applyYearToWorks}
                    onChange={e => setApplyYearToWorks(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="font-semibold">Renseigner une année de création commune</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Recommandé : sans année, les œuvres du lot s’affichent en « Inconnu » dans la répartition par période. L’année sera écrite sur chaque fiche du lot.
                    </span>
                  </span>
                </label>

                {applyYearToWorks && (
                  <div className="mt-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="2100"
                      value={lotYear}
                      onChange={e => setLotYear(e.target.value)}
                      placeholder="Ex : 2010"
                      className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>
                )}
              </div>


              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          {step === "select" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => setStep("configure")}
                disabled={selected.size < 2}
                className="px-5 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-40"
              >
                Suivant — {selected.size} œuvre{selected.size > 1 ? "s" : ""} sélectionnée{selected.size > 1 ? "s" : ""}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep("select")}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                ← Retour
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !lotTitle.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-40"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving
                  ? "Enregistrement…"
                  : isEditMode
                  ? `Enregistrer (${selected.size} œuvre${selected.size > 1 ? "s" : ""})`
                  : `Créer le lot (${selected.size} œuvre${selected.size > 1 ? "s" : ""})`
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}